// AdminBroadcastTab — admin/super-admin manual notification broadcaster.
//
// Compose form on the left, history panel on the right.
// Uses the same dispatcher as automatic triggers, so notifications appear
// in the user's bell + email + push depending on their preferences.
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import AppModal from '../ui/AppModal';

const CATEGORIES = [
  { v: 'operations', label: 'Operations' },
  { v: 'tournament', label: 'Tournament' },
  { v: 'live_match', label: 'Live Match' },
  { v: 'auction',    label: 'Auction' },
  { v: 'booking',    label: 'Booking' },
  { v: 'payment',    label: 'Payment' },
  { v: 'shop',       label: 'Shop' },
];

const AUDIENCE_TYPES = [
  { v: 'user',     label: 'Single user',          needs: 'user' },
  { v: 'role',     label: 'By role',              needs: 'role' },
  { v: 'event',    label: 'Event participants',   needs: 'event' },
  { v: 'members',  label: 'Active members',       needs: null },
  { v: 'everyone', label: 'EVERYONE (super)',     needs: null },
];

const ROLES = ['regular', 'admin', 'super_admin'];

function fmtDate(iso) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminBroadcastTab({ isSuperAdmin }) {
  const [category, setCategory] = useState('operations');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [audienceType, setAudienceType] = useState('role');
  const [audienceValue, setAudienceValue] = useState('admin');
  const [count, setCount] = useState(null);    // null=unknown, num=resolved
  const [sample, setSample] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);

  // Lookups
  useEffect(() => {
    axiosInstance.get('/events').then((r) => setEvents(r.data || [])).catch(() => {});
    if (isSuperAdmin) {
      axiosInstance.get('/admin/users').then((r) => setUsers(r.data || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  const loadHistory = async () => {
    try {
      const r = await axiosInstance.get('/admin/notifications/broadcasts');
      setHistory(r.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  // Reset audience value when type changes
  useEffect(() => {
    if (audienceType === 'role')   setAudienceValue('admin');
    else if (audienceType === 'user' || audienceType === 'event') setAudienceValue('');
    else setAudienceValue('');
    setCount(null);
    setSample([]);
  }, [audienceType]);

  const previewAudience = async () => {
    setPreviewing(true);
    try {
      const r = await axiosInstance.post('/admin/notifications/audience-preview', {
        type: audienceType,
        value: audienceValue || null,
      });
      setCount(r.data.count);
      setSample(r.data.sample || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setCount(null);
    } finally { setPreviewing(false); }
  };

  // Auto-preview when audience changes
  useEffect(() => {
    const needsValue = AUDIENCE_TYPES.find((a) => a.v === audienceType)?.needs;
    if (needsValue && !audienceValue) return;
    previewAudience();
  }, [audienceType, audienceValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSend = title.trim() && body.trim() && count !== null && count > 0 && !sending;

  const submitSend = async () => {
    setSending(true);
    try {
      const r = await axiosInstance.post('/admin/notifications/broadcast', {
        category,
        title: title.trim(),
        body: body.trim(),
        deep_link: deepLink.trim() || null,
        audience: { type: audienceType, value: audienceValue || null },
      });
      const stats = r.data.stats || {};
      const inAppSent = stats.in_app?.sent || 0;
      toast.success(`Broadcast sent — ${inAppSent}/${r.data.recipient_count} delivered in-app`);
      // Reset form
      setTitle(''); setBody(''); setDeepLink('');
      setConfirming(false);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-broadcast-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* COMPOSE */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
        <h3 className="font-extrabold text-[#2B3A67] text-sm flex items-center gap-2">
          <i className="ph-bold ph-megaphone-simple" /> New broadcast
        </h3>

        <Field label="Category">
          <select
            data-testid="broadcast-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
          >
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </Field>

        <Field label="Title" hint={`${title.length}/100`}>
          <input
            data-testid="broadcast-title"
            type="text"
            maxLength={100}
            placeholder="e.g. Venue change for tomorrow's match"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
          />
        </Field>

        <Field label="Body" hint={`${body.length}/500`}>
          <textarea
            data-testid="broadcast-body"
            rows={4}
            maxLength={500}
            placeholder="Full message visible to recipients..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
          />
        </Field>

        <Field label="Deep link (optional)" hint="Where the notification opens when tapped">
          <input
            data-testid="broadcast-deep-link"
            type="text"
            placeholder="/tournaments/abc123 or /shop/products/xyz"
            value={deepLink}
            onChange={(e) => setDeepLink(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
          />
        </Field>

        <Field label="Audience">
          <select
            data-testid="broadcast-audience-type"
            value={audienceType}
            onChange={(e) => setAudienceType(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562] mb-2"
          >
            {AUDIENCE_TYPES.filter((a) => isSuperAdmin || a.v !== 'everyone').map((a) => (
              <option key={a.v} value={a.v}>{a.label}</option>
            ))}
          </select>

          {audienceType === 'role' && (
            <select
              data-testid="broadcast-audience-role"
              value={audienceValue}
              onChange={(e) => setAudienceValue(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}

          {audienceType === 'event' && (
            <select
              data-testid="broadcast-audience-event"
              value={audienceValue}
              onChange={(e) => setAudienceValue(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
            >
              <option value="">— pick event —</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          )}

          {audienceType === 'user' && (
            <input
              data-testid="broadcast-audience-user"
              type="text"
              placeholder="Paste user_id (find in /admin → Users)"
              value={audienceValue}
              onChange={(e) => setAudienceValue(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#E54562]"
            />
          )}

          {/* Recipient count */}
          <div data-testid="broadcast-recipient-count" className="mt-2 text-xs">
            {previewing ? (
              <span className="text-gray-400">Counting…</span>
            ) : count === null ? (
              <span className="text-gray-400">Pick an audience to see recipient count.</span>
            ) : count === 0 ? (
              <span className="text-red-600 font-bold">0 recipients — nothing to send.</span>
            ) : (
              <span className="text-green-700 font-bold">
                Will send to {count.toLocaleString()} {count === 1 ? 'user' : 'users'}
                {sample.length > 0 && (
                  <span className="text-gray-500 font-normal">
                    {' '}— sample: {sample.map((s) => s.name || s.email).join(', ')}{count > sample.length ? '…' : ''}
                  </span>
                )}
              </span>
            )}
          </div>
        </Field>

        <button
          data-testid="broadcast-send-btn"
          onClick={() => setConfirming(true)}
          disabled={!canSend}
          className="w-full py-3 rounded-2xl bg-[#E54562] text-white font-extrabold text-sm shadow-md hover:bg-[#d13a55] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Review & Send
        </button>
      </div>

      {/* HISTORY */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-extrabold text-[#2B3A67] text-sm flex items-center gap-2">
            <i className="ph-bold ph-clock-counter-clockwise" /> History
          </h3>
          <button
            data-testid="broadcast-refresh-history"
            onClick={loadHistory}
            className="text-xs text-gray-500 hover:text-[#2B3A67]"
          >Refresh</button>
        </div>
        {history.length === 0 ? (
          <p data-testid="broadcast-history-empty" className="text-xs text-gray-400 py-6 text-center">
            No broadcasts sent yet.
          </p>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {history.map((b) => {
              const total = b.recipient_count || 0;
              const inApp = b.stats?.in_app || {};
              const email = b.stats?.email || {};
              const push  = b.stats?.push  || {};
              return (
                <div key={b.id} data-testid={`broadcast-row-${b.id}`} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="font-bold text-sm text-[#2B3A67] truncate">{b.title}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtDate(b.created_at)}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">{b.body}</p>
                  <div className="flex flex-wrap gap-2 text-[10px] font-bold">
                    <Badge label={`${total} ppl`} bg="bg-gray-200" />
                    <Badge label={`${b.audience_type}${b.audience_value ? `:${b.audience_value}` : ''}`} bg="bg-blue-100 text-blue-700" />
                    <Badge label={`In-app ${inApp.sent || 0}/${total}`} bg="bg-green-100 text-green-700" />
                    <Badge label={`Email ${email.sent || 0}/${total}`} bg={(email.sent || 0) > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'} />
                    <Badge label={`Push ${push.sent || 0}/${total}`} bg={(push.sent || 0) > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'} />
                  </div>
                  {b.sender_name && (
                    <p className="text-[10px] text-gray-400 mt-1.5">by {b.sender_name}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CONFIRMATION MODAL */}
      <AppModal
        open={confirming}
        onClose={() => !sending && setConfirming(false)}
        title="Send this broadcast?"
        size="md"
        testId="broadcast-confirm-modal"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            <i className="ph-bold ph-warning mr-1" />
            This will deliver to <b>{count?.toLocaleString()}</b> users via in-app + email + push (per their preferences).
            {audienceType === 'everyone' && <p className="mt-1 font-bold">⚠ EVERYONE includes every user account.</p>}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 text-xs space-y-1.5">
            <p><span className="text-gray-500">Title:</span> <b>{title}</b></p>
            <p className="whitespace-pre-wrap text-gray-700">{body}</p>
            {deepLink && <p className="text-gray-500">Deep link: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{deepLink}</code></p>}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="broadcast-confirm-cancel"
              onClick={() => setConfirming(false)}
              disabled={sending}
              className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200"
            >Cancel</button>
            <button
              data-testid="broadcast-confirm-send"
              onClick={submitSend}
              disabled={sending}
              className="flex-1 py-2.5 rounded-xl bg-[#E54562] text-white font-bold text-sm hover:bg-[#d13a55] disabled:opacity-60"
            >
              {sending ? 'Sending…' : `Send to ${count}`}
            </button>
          </div>
        </div>
      </AppModal>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</label>
        {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Badge({ label, bg }) {
  return <span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${bg}`}>{label}</span>;
}
